import SlideIn from 'app/components/SlideIn/SlideIn';
import { useUpdateServiceProfile } from 'app/hooks/requests/commerce';
import { useVerifyBank } from 'app/hooks/requests/finance';
import { BankDetails } from 'app/utilities/types/shared';
import CheckCircle from 'assets/images/CheckCircle.svg';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ElrBankSelect, ElrButton, ElrInput, ElrSwitch } from 'ui-components';
import * as Yup from 'yup';

const NUBAN_DIGIT_LENGTH = 10;

const validationSchema = Yup.object({
  bank: Yup.object().shape({
    value: Yup.string().required('Bank name is required'),
    code: Yup.string().required('Bank code is required'),
  }),
  accountNumber: Yup.string()
    .required('Account number is required')
    .matches(/^[0-9]{10}$/, 'Account number must be 10 digits'),
  autoPayout: Yup.boolean().required('Auto payout is required'),
});

interface SettingsProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  outletType: string;
  outletTypeId: string;
  payload: {
    bankDetails?: BankDetails;
    autoPayout?: boolean;
  };
}

const Settings: React.FC<SettingsProps> = ({
  isOpen,
  setIsOpen,
  outletType,
  outletTypeId,
  payload: propPayload,
}) => {
  const { handleUpdateServiceProfile, isLoading } = useUpdateServiceProfile();

  const [fetchBankName, isFetching] = useVerifyBank();

  const [accountName, setAccountName] = useState<string>('');

  const { handleSubmit, values, setFieldValue, isValid } = useFormik({
    initialValues: {
      accountNumber: propPayload.bankDetails?.accountNumber || '',
      bank: {
        value: propPayload.bankDetails?.bankName || '',
        code: propPayload.bankDetails?.bankCode || '',
      },
      autoPayout: propPayload.autoPayout || false,
    },
    validationSchema,
    validateOnChange: true,
    onSubmit: async () => {
      try {
        const payload = {
          autoPayout: values.autoPayout,
          bankDetails: {
            accountName: values.bank.value,
            accountNumber: values.accountNumber,
            bankName: values.bank.value,
            bankCode: values.bank.code,
          },
          outletType,
          outletTypeId,
        };
        await handleUpdateServiceProfile(payload);
        setIsOpen(false);
      } catch (error) {
        console.error(error);
      }
    },
  });

  const handleVerifyBank = async () => {
    if (!values.bank.code) {
      toast.error('Please select bank', {
        closeOnClick: true,
        autoClose: 3000,
      });
      return;
    }
    if (values.accountNumber.length === NUBAN_DIGIT_LENGTH) {
      const response = await fetchBankName({
        accountNumber: values.accountNumber,
        bankCode: values.bank.code,
      });

      if (response.status) {
        setFieldValue('bank', {
          ...values.bank,
          accountName: response.account_name,
        });
        setAccountName(response.account_name);
      }
    }
  };

  useEffect(() => {
    if (!isValid) {
      setAccountName('');
    }
    if (
      isValid &&
      values.accountNumber &&
      values.accountNumber.length === NUBAN_DIGIT_LENGTH &&
      !isFetching
    ) {
      handleVerifyBank();
    }
  }, [isValid]);

  const bankDetailsContent = (
    <div className="flex flex-col w-full gap-5">
      <div className="w-full">
        <p className="pb-2 text-base text-elr-black-400">Bank Name</p>
        <ElrBankSelect
          placeholder="Bank name"
          onChange={(bank) => setFieldValue('bank', bank)}
          currentValue={values.bank.value}
        />
      </div>

      <div className="w-full">
        <p className="pb-2 text-base text-elr-black-400">Account Number</p>
        <ElrInput
          className="h-12 border-none outline-none bg-elr-gray"
          value={values.accountNumber}
          onChange={(e) =>
            setFieldValue('accountNumber', e.target.value.replace(/\D/g, ''))
          }
          placeholder="Account Number"
        />
      </div>
      {accountName && (
        <div className="flex flex-row items-center gap-x-1">
          <img src={CheckCircle} alt="check-circle" />
          <p className="pt-0.5 text-base text-elr-black">{accountName}</p>
        </div>
      )}
      {isFetching && (
        <div className="flex flex-row items-center gap-x-1">
          <div className="w-4 h-4 bg-elr-black-400 rounded-full animate-pulse animate-pulse-delay-1000" />
          <div className="w-4 h-4 bg-elr-black-400 rounded-full animate-pulse animate-pulse-delay-2000" />
          <div className="w-4 h-4 bg-elr-black-400 rounded-full animate-pulse animate-pulse-delay-3000" />
        </div>
      )}
    </div>
  );

  return (
    <div>
      <SlideIn close={() => setIsOpen(false)} shown={isOpen}>
        <form onSubmit={handleSubmit}>
          <div className="flex flex-col gap-4 py-4">
            <h1 className="text-2xl font-medium text-elr-black">Settings</h1>
            <p className="text-elr-black-400">
              Manage your settings and preferences.
            </p>
          </div>

          {bankDetailsContent}

          <div className="flex flex-row items-center justify-between gap-x-4 bg-elr-gray mt-10 p-4 rounded-md">
            <p>
              Receive payouts automatically to the bank account information
              provided
            </p>
            <ElrSwitch
              isToggled={values.autoPayout}
              toggle={() => setFieldValue('autoPayout', !values.autoPayout)}
            />
          </div>
          <ElrButton
            className="mt-10 w-full bg-elr-black text-elr-white rounded-md"
            text="Save"
            spinnerColor="White"
            submit
            loading={isLoading}
            disabled={!isValid || isLoading}
          />
        </form>
      </SlideIn>
    </div>
  );
};

export default Settings;
